import { resolveAnswerGeometryProjection } from 'lib/answerGeometryProjection'
import {
    answerImageInputClassificationItem,
    answerRecordingInputClassificationItem,
    aiInputDescriptorsForAnswer,
    aiRunOutputIsConsumedInput,
    fileInputClassificationItem,
    mediaEntryIsAiInput,
} from 'lib/aiInputMediaClassification'
import { payloadMediaKind } from 'components/answers/Answer/payloadMediaKind'
import { combinedMediaItemReady } from 'lib/combinedMedia'
import { firstValidMediaTimestamp, sortMediaByCreation } from 'lib/mediaDisplayOrder'

// Reserved heights made row height a pure function of the answer payload (see
// answerPlaceholderHeight/questStreamHeight). That function is only as good as its input: when
// an answer is evicted from the entity cache, remounting rows receive a bare
// `{ id, _placeholder: true }` stub and the estimate collapses to the text fallback — then
// corrects by the full media height when hydration lands, which is exactly the scroll jump the
// reservations removed. This module retains the ESTIMATOR'S INPUTS (a ~10-scalar,
// answer-shaped "height fingerprint") across eviction, so the pure function stays exact.
//
// Deliberately NOT part of the answer cache: any renderable-looking field on a cache entry
// flips isHydratedAnswerCacheEntry(), so the shadow lives here and estimators consult it by id.
// Storing inputs (not measured outputs, cf. answerHeightMemory) means there is no fingerprint
// invalidation problem: estimator changes and width changes apply automatically.

export const MAX_ANSWER_HEIGHT_FINGERPRINTS = 5000
const FINGERPRINT_TEXT_LIMIT = 1200 // covers the ~15-line collapse clamp at ~72 chars/line
const FINGERPRINT_URL_TEXT_LIMIT = 400 // tweet bodies truncate at 280 chars
const FINGERPRINT_MAX_MEDIA_ITEMS = 4 // render grids cap at 4 tiles
const FINGERPRINT_MAX_AI_INPUT_ITEMS = 6 // the compact provenance strip shows at most 6
const FINGERPRINT_MAX_TWEET_URL_ENTITIES = 8 // X repeats one t.co per photo (≤4) plus a few links

const fingerprints = new Map<string, Record<string, unknown>>()

const positiveNumber = (value: unknown): number | null => {
    const n = Number(value)
    return Number.isFinite(n) && n > 0 ? Math.round(n) : null
}

const truncated = (value: unknown, limit: number): string | null => {
    if (typeof value !== 'string' || !value) return null
    return value.length > limit ? value.slice(0, limit) : value
}

const hasArrayItems = (value: unknown): value is unknown[] =>
    Array.isArray(value) && value.length > 0

const mediaIdentityUrl = (item: any): string | null => {
    if (!item || typeof item !== 'object') return null

    const urls = item.urls && typeof item.urls === 'object' ? item.urls : {}
    const previewUrls =
        item.preview_urls && typeof item.preview_urls === 'object' ? item.preview_urls : {}

    for (const value of [
        item.url,
        item.preview_url,
        item.previewUrl,
        item.image_url,
        item.imageUrl,
        item.thumbnail_url,
        item.thumbnailUrl,
        urls.original,
        urls.large,
        urls.medium,
        urls.small,
        previewUrls.original,
        previewUrls.large,
        previewUrls.medium,
        previewUrls.small,
    ]) {
        if (typeof value === 'string' && value) return value
    }

    return null
}

const dimsOnly = (item: any, widthKey: string, heightKey: string) => {
    const width = positiveNumber(item?.[widthKey])
    const height = positiveNumber(item?.[heightKey])
    const out: Record<string, unknown> =
        width && height ? { [widthKey]: width, [heightKey]: height } : {}
    // Media identity survives even when dims don't so placeholder fingerprints preserve the
    // same shape as the source answer. Static estimators still keep dimensionless media on the
    // reserved fallback bucket rather than deriving geometry from client-side image loads.
    if (item?.blob_id) out.blob_id = item.blob_id
    if (item?.attachment_id) out.attachment_id = item.attachment_id
    if ((!width || !height) && !out.blob_id && !out.attachment_id) {
        const url = mediaIdentityUrl(item)
        if (url) out.url = url
    }
    return out
}

const sentinelArray = (value: unknown, fill: unknown) =>
    hasArrayItems(value) ? value.slice(0, FINGERPRINT_MAX_MEDIA_ITEMS).map(() => fill) : null

const FINGERPRINT_FILE_FIELD_LIMIT = 120

// file_urls entries need more than a bare sentinel: the estimator's file term dispatches on
// FileEmbed's type predicate (tallFileEmbedKind in fileEmbedMediaClassification — mime + file
// extension), so retain exactly those inputs. `content_type` keeps the first non-empty mime
// field and `name` the first non-empty filename candidate (reduced to its query-stripped
// basename) in the SAME precedence order the predicate reads, so classifying the fingerprint
// yields the same result as classifying the original entry.
const fileUrlHeightFingerprint = (
    file: any,
    aiInputDescriptors: any[],
): Record<string, unknown> => {
    const out: Record<string, unknown> = {}
    if (!file || typeof file !== 'object') return out

    const contentType = [file.content_type, file.contentType, file.mime, file.type].find(
        value => typeof value === 'string' && value,
    )
    if (contentType) out.content_type = truncated(contentType, FINGERPRINT_FILE_FIELD_LIMIT)

    const nameCandidate = [
        file.name,
        file.filename,
        file.original_filename,
        file.url,
        file.file_url,
    ].find(value => typeof value === 'string' && value)
    if (nameCandidate) {
        const withoutQuery = String(nameCandidate).split(/[?#]/)[0]
        const basename = withoutQuery.split('/').pop() || withoutQuery
        out.name = truncated(basename, FINGERPRINT_FILE_FIELD_LIMIT)
    }
    // A minted HTML file renders its inscription window instead of the chip.
    if (file.bsv_ordinal?.id) out.bsv_ordinal = { id: file.bsv_ordinal.id }

    if (mediaEntryIsAiInput(fileInputClassificationItem(file), aiInputDescriptors)) {
        out.ai_input = 1
    }

    return out
}

const legacyImageDimsOnly = (image: any) => dimsOnly(image, 'image_width', 'image_height')

// Input media is filtered before the four-item output cap. Retain both independent windows so
// six sources cannot crowd a rendered output out of the eviction fingerprint.
const retainHeightRelevantMediaEntries = (entries: any[], isInput: (entry: any) => boolean) => {
    let inputCount = 0
    let outputCount = 0
    return entries.filter(entry => {
        if (isInput(entry)) {
            inputCount += 1
            return inputCount <= FINGERPRINT_MAX_AI_INPUT_ITEMS
        }
        outputCount += 1
        return outputCount <= FINGERPRINT_MAX_MEDIA_ITEMS
    })
}

const mediaTimingFingerprint = (item: any, fallback: any = null) => {
    const createdAt = firstValidMediaTimestamp(item?.created_at, item?.createdAt)
    const attachedAt = firstValidMediaTimestamp(item?.attached_at, item?.attachedAt)
    const blobCreatedAt = firstValidMediaTimestamp(item?.blob_created_at, item?.blobCreatedAt)
    const fallbackAt =
        createdAt || attachedAt || blobCreatedAt ? null : firstValidMediaTimestamp(fallback)

    return {
        ...(createdAt || fallbackAt ? { created_at: String(createdAt || fallbackAt) } : {}),
        ...(attachedAt ? { attached_at: String(attachedAt) } : {}),
        ...(blobCreatedAt ? { blob_created_at: String(blobCreatedAt) } : {}),
    }
}

// getTweetDisplayText swaps each t.co url entity for its display_url and drops the trailing
// media/quote-card links before the line count, so the estimate needs the url entities of the
// text it renders (the abbreviated `text` or the long-post `note_tweet`). Mentions and hashtags
// keep their literal length and are not geometry inputs.
const tweetEntitiesFingerprint = (entities: any): Record<string, unknown> | null => {
    if (!hasArrayItems(entities?.urls)) return null

    const urls = entities.urls.slice(0, FINGERPRINT_MAX_TWEET_URL_ENTITIES).map((entity: any) => {
        const out: Record<string, unknown> = {}
        if (Number.isInteger(entity?.start)) out.start = entity.start
        if (Number.isInteger(entity?.end)) out.end = entity.end
        if (typeof entity?.display_url === 'string') out.display_url = entity.display_url
        if (typeof entity?.expanded_url === 'string') out.expanded_url = entity.expanded_url
        if (entity?.media_key) out.media_key = String(entity.media_key)
        return out
    })
    return { urls }
}

const tweetServiceJsonFingerprint = (serviceJson: any): Record<string, unknown> | null => {
    if (!serviceJson || typeof serviceJson !== 'object') return null

    const out: Record<string, unknown> = {}
    if (serviceJson.id) out.id = serviceJson.id
    if (serviceJson.error) out.error = true
    // The card renders a date row whenever the post carries created_at (+25px).
    if (serviceJson.created_at) out.created_at = 'fp'
    const text = truncated(serviceJson.text, FINGERPRINT_URL_TEXT_LIMIT)
    if (text) out.text = text
    const entities = tweetEntitiesFingerprint(serviceJson.entities)
    if (entities) out.entities = entities
    // The "more" toggle renders the FULL note text, so its fingerprint keeps the same budget as
    // answer bodies — the 280-char tweet limit would cap expanded re-estimates.
    const noteText = truncated(serviceJson.note_tweet?.text, FINGERPRINT_TEXT_LIMIT)
    if (noteText) {
        const noteEntities = tweetEntitiesFingerprint(serviceJson.note_tweet?.entities)
        out.note_tweet = { text: noteText, ...(noteEntities ? { entities: noteEntities } : {}) }
    }
    return Object.keys(out).length ? out : null
}

const urlFingerprint = (
    url: any,
    visibleSelection: unknown = null,
): Record<string, unknown> | null => {
    if (!url || typeof url !== 'object') return null
    const out: Record<string, unknown> = {}
    if (typeof url.address === 'string') out.address = url.address
    if (url.tweet_id) out.tweet_id = url.tweet_id
    if (url.video_id) out.video_id = url.video_id
    if (url.service_name) out.service_name = url.service_name
    if (url.service_status) out.service_status = url.service_status
    if (url.service_id) out.service_id = url.service_id
    if (url.knov_app) out.knov_app = url.knov_app
    const selection = truncated(url.selection, FINGERPRINT_URL_TEXT_LIMIT)
    if (selection) out.selection = selection
    if (typeof visibleSelection === 'string') {
        out.visible_selection = truncated(visibleSelection, FINGERPRINT_URL_TEXT_LIMIT) || ''
    }
    const serviceJson = tweetServiceJsonFingerprint(url.service_json)
    if (serviceJson) out.service_json = serviceJson
    // TwitterUrl renders url.embed as a nested quote-tweet window or the constant website card.
    // Quote height is content-derived, so retain the same text, load state, and media dimensions
    // used by tweetCardHeight. This keeps evicted placeholder rows on the exact rendered reserve.
    if (url.embed && typeof url.embed === 'object') {
        const embedOut: Record<string, unknown> = { present: true }
        const embed = url.embed
        if (typeof embed.address === 'string') embedOut.address = embed.address
        if (embed.tweet_id) embedOut.tweet_id = embed.tweet_id
        if (embed.service_name) embedOut.service_name = embed.service_name
        if (embed.service_status) embedOut.service_status = embed.service_status
        if (embed.service_id) embedOut.service_id = embed.service_id
        const embedSelection = truncated(embed.selection, FINGERPRINT_URL_TEXT_LIMIT)
        if (embedSelection) embedOut.selection = embedSelection
        const embedServiceJson = tweetServiceJsonFingerprint(embed.service_json)
        if (embedServiceJson) embedOut.service_json = embedServiceJson
        const embedImageUrls = sentinelArray(embed.image_urls, 'fp')
        if (embedImageUrls) embedOut.image_urls = embedImageUrls
        const embedVideoUrls = sentinelArray(embed.video_urls, 'fp')
        if (embedVideoUrls) embedOut.video_urls = embedVideoUrls
        if (hasArrayItems(embed.image_metadata)) {
            embedOut.image_metadata = embed.image_metadata
                .slice(0, FINGERPRINT_MAX_MEDIA_ITEMS)
                .map((m: any) => dimsOnly(m, 'width', 'height'))
        }
        if (hasArrayItems(embed.video_metadata)) {
            embedOut.video_metadata = embed.video_metadata
                .slice(0, FINGERPRINT_MAX_MEDIA_ITEMS)
                .map((m: any) => dimsOnly(m, 'width', 'height'))
        }
        if (embed.tweet_media_pending === true) embedOut.tweet_media_pending = true
        out.embed = embedOut
    }
    // Pending media reserves tiles from the metadata dims; the flag decides whether they render.
    if (url.tweet_media_pending === true) out.tweet_media_pending = true
    const imageUrls = sentinelArray(url.image_urls, 'fp')
    if (imageUrls) out.image_urls = imageUrls
    const videoUrls = sentinelArray(url.video_urls, 'fp')
    if (videoUrls) out.video_urls = videoUrls
    if (hasArrayItems(url.image_metadata)) {
        out.image_metadata = url.image_metadata
            .slice(0, FINGERPRINT_MAX_MEDIA_ITEMS)
            .map((m: any) => dimsOnly(m, 'width', 'height'))
    }
    if (hasArrayItems(url.video_metadata)) {
        out.video_metadata = url.video_metadata
            .slice(0, FINGERPRINT_MAX_MEDIA_ITEMS)
            .map((m: any) => dimsOnly(m, 'width', 'height'))
    }
    return Object.keys(out).length ? out : null
}

// Fields that let the estimators produce a non-fallback height. A bare `{id, _placeholder}`
// stub has none of these.
export function answerLooksHeightEstimable(answer: any): boolean {
    if (!answer || typeof answer !== 'object') return false
    if (answer._placeholder) return false
    return !!(
        answer.delta_json ||
        answer.content ||
        answer.content_preview ||
        answer.display_content ||
        answer.url ||
        answer.answer_image_url ||
        answer.local_answer_image_url ||
        answer.recording_url ||
        answer.mp4_recording_url ||
        answer.recording_thumbnail_url ||
        hasArrayItems(answer.images_v2) ||
        hasArrayItems(answer.image_urls) ||
        hasArrayItems(answer.file_urls) ||
        hasArrayItems(answer.embeds) ||
        hasArrayItems(answer.ai_runs) ||
        typeof answer.post_answer_has_content === 'boolean' ||
        (answer.message_type === 'poll' && hasArrayItems(answer.poll_options_payload))
    )
}

// Answer-shaped subset containing ONLY what the height estimators read, with large content
// strings truncated and every media list reduced to dims/sentinels plus stable identity.
export function extractAnswerHeightFingerprint(answer: any): Record<string, unknown> | null {
    if (!answerLooksHeightEstimable(answer)) return null

    const out: Record<string, unknown> = { id: answer.id }
    if (hasArrayItems(answer.payment_receipts)) {
        out.payment_receipts = answer.payment_receipts.map((receipt: any) => ({
            outputs: Array.from({ length: receipt.outputs?.length || 0 }, () => ({})),
        }))
    }
    const projection = resolveAnswerGeometryProjection(answer)

    if (answer.height_content_signature) {
        out.height_content_signature = String(answer.height_content_signature)
    }

    const content = truncated(projection?.text, FINGERPRINT_TEXT_LIMIT)
    if (content) out.content = content
    // Plain fingerprint content reproduces the renderer gate by itself. Retain a sentinel only
    // for shapes where display text and PostAnswer content disagree (notably inline-image-only
    // deltas, or preview-only stubs) so existing fingerprints and height signatures stay stable.
    const fingerprintContentImpliesPostAnswerContent = !!content && /\S/.test(content)
    const postAnswerContentNeedsSentinel =
        typeof projection?.hasPostAnswerContent === 'boolean' &&
        projection.hasPostAnswerContent !== fingerprintContentImpliesPostAnswerContent
    if (postAnswerContentNeedsSentinel) {
        out.post_answer_has_content = projection.hasPostAnswerContent
    }

    for (const key of ['image_width', 'image_height', 'video_width', 'video_height']) {
        const value = positiveNumber(answer[key])
        if (value) out[key] = value
    }

    // AI-input classification is persisted as `ai_input: 1` flags: fingerprinted ai_runs drop
    // action_payload, so the shared matcher (lib/aiInputMediaClassification) cannot re-run on a
    // stub — the projection honors the flag instead.
    const aiInputDescriptors = aiInputDescriptorsForAnswer(answer)
    if (hasArrayItems(answer.images_v2)) {
        out.images_v2 = retainHeightRelevantMediaEntries(
            sortMediaByCreation(answer.images_v2),
            (image: any) => mediaEntryIsAiInput(image, aiInputDescriptors),
        ).map((image: any) => ({
            ...dimsOnly(image, 'image_width', 'image_height'),
            ...mediaTimingFingerprint(image),
            ...(mediaEntryIsAiInput(image, aiInputDescriptors) ? { ai_input: 1 } : {}),
        }))
    }
    if (hasArrayItems(answer.image_urls)) {
        out.image_urls = retainHeightRelevantMediaEntries(
            sortMediaByCreation(answer.image_urls),
            (image: any) => mediaEntryIsAiInput(image, aiInputDescriptors),
        ).map((image: any) => ({
            ...legacyImageDimsOnly(image),
            ...mediaTimingFingerprint(image),
            ...(mediaEntryIsAiInput(image, aiInputDescriptors) ? { ai_input: 1 } : {}),
        }))
    }

    if (
        (answer.answer_image_url || answer.local_answer_image_url) &&
        mediaEntryIsAiInput(answerImageInputClassificationItem(answer), aiInputDescriptors)
    ) {
        out.answer_image_ai_input = 1
    }
    if (
        (answer.recording_url || answer.mp4_recording_url) &&
        mediaEntryIsAiInput(answerRecordingInputClassificationItem(answer), aiInputDescriptors)
    ) {
        out.recording_ai_input = 1
    }

    for (const key of [
        'answer_image_url',
        'local_answer_image_url',
        'recording_url',
        'mp4_recording_url',
        'recording_thumbnail_url',
    ]) {
        if (answer[key]) out[key] = 'fp'
    }
    for (const key of ['recording_type', 'original_recording_type', 'recording_content_type']) {
        if (answer[key]) out[key] = String(answer[key])
    }

    const url = urlFingerprint(answer.url, projection?.url?.visibleSelectionText)
    if (url) out.url = url

    // In-content embed links (stream/leaderboard/TDP widgets + the text-derived tweet preview)
    // extract from delta_json's link attributes and embed ops, which the plain-text `content`
    // field does NOT capture. Retain the already suppression-filtered extraction output so the
    // projection's in-content term stays exact for evicted stubs without rescanning delta_json.
    const contentEmbedLinks = projection?.contentEmbeds?.links
    if (
        contentEmbedLinks &&
        (contentEmbedLinks.stream.length ||
            contentEmbedLinks.leaderboard.length ||
            contentEmbedLinks.tdp.length ||
            contentEmbedLinks.market.length ||
            contentEmbedLinks.twitter.length ||
            contentEmbedLinks.streamInvite ||
            contentEmbedLinks.dailyEarnings ||
            contentEmbedLinks.hostRevenue)
    ) {
        const contentEmbedLinksOut: Record<string, unknown> = {}
        if (contentEmbedLinks.stream.length) {
            contentEmbedLinksOut.stream = [...contentEmbedLinks.stream]
        }
        if (contentEmbedLinks.leaderboard.length) {
            contentEmbedLinksOut.leaderboard = [...contentEmbedLinks.leaderboard]
        }
        if (contentEmbedLinks.tdp.length) {
            contentEmbedLinksOut.tdp = [...contentEmbedLinks.tdp]
        }
        if (contentEmbedLinks.market.length) {
            contentEmbedLinksOut.market = [...contentEmbedLinks.market]
        }
        if (contentEmbedLinks.twitter.length) {
            contentEmbedLinksOut.twitter = [...contentEmbedLinks.twitter]
        }
        if (contentEmbedLinks.streamInvite) contentEmbedLinksOut.stream_invite = true
        if (contentEmbedLinks.dailyEarnings) contentEmbedLinksOut.daily_earnings = true
        if (contentEmbedLinks.hostRevenue) contentEmbedLinksOut.host_revenue = true
        out.content_embed_links = contentEmbedLinksOut
    }

    // Hiding/unhiding a preview changes rendered height (both for in-content embeds — already
    // reflected above post-suppression — and for the attached-url preview), so a cheap sentinel
    // keeps the content signature honest without persisting the urls themselves.
    const suppressedPreviewUrls = sentinelArray(answer.suppressed_preview_urls, 'fp')
    if (suppressedPreviewUrls) out.suppressed_preview_urls = suppressedPreviewUrls

    if (hasArrayItems(answer.embeds)) {
        // PostEmbed renders a full nested answer whose reserve models media tiles from the
        // embeds[] entry, so keep the same dims the embedded-answer estimator reads.
        out.embeds = answer.embeds.slice(0, FINGERPRINT_MAX_MEDIA_ITEMS).map((embed: any) => {
            const embedOut: Record<string, unknown> = {}
            const embedContent =
                truncated(embed?.content, FINGERPRINT_URL_TEXT_LIMIT) ||
                truncated(embed?.display_content, FINGERPRINT_URL_TEXT_LIMIT)
            if (embedContent) embedOut.content = embedContent
            if (hasArrayItems(embed?.images_v2)) {
                embedOut.images_v2 = sortMediaByCreation(embed.images_v2)
                    .slice(0, FINGERPRINT_MAX_MEDIA_ITEMS)
                    .map((image: any) => ({
                        ...dimsOnly(image, 'image_width', 'image_height'),
                        ...mediaTimingFingerprint(image),
                    }))
            }
            if (hasArrayItems(embed?.image_urls)) {
                embedOut.image_urls = sortMediaByCreation(embed.image_urls)
                    .slice(0, FINGERPRINT_MAX_MEDIA_ITEMS)
                    .map((image: any) => ({
                        ...legacyImageDimsOnly(image),
                        ...mediaTimingFingerprint(image),
                    }))
            }
            for (const key of [
                'answer_image_url',
                'local_answer_image_url',
                'recording_url',
                'mp4_recording_url',
                'recording_thumbnail_url',
            ]) {
                if (embed?.[key]) embedOut[key] = 'fp'
            }
            for (const key of [
                'recording_type',
                'original_recording_type',
                'recording_content_type',
            ]) {
                if (embed?.[key]) embedOut[key] = String(embed[key])
            }
            for (const key of ['image_width', 'image_height', 'video_width', 'video_height']) {
                const value = positiveNumber(embed?.[key])
                if (value) embedOut[key] = value
            }
            return embedOut
        })
    }

    if (hasArrayItems(answer.file_urls)) {
        out.file_urls = retainHeightRelevantMediaEntries(answer.file_urls, (file: any) =>
            mediaEntryIsAiInput(fileInputClassificationItem(file), aiInputDescriptors),
        ).map((file: any) => fileUrlHeightFingerprint(file, aiInputDescriptors))
    }

    if (answer.message_type === 'poll' && hasArrayItems(answer.poll_options_payload)) {
        out.message_type = 'poll'
        out.poll_options_payload = answer.poll_options_payload.map(() => ({}))
    }

    if (hasArrayItems(answer.ai_runs)) {
        out.ai_runs = answer.ai_runs.slice(0, FINGERPRINT_MAX_MEDIA_ITEMS).map((run: any) => ({
            ...(run?.action_tag ? { action_tag: run.action_tag } : {}),
            ...(run?.actionTag ? { actionTag: run.actionTag } : {}),
            ...(run?.action_index != null ? { action_index: run.action_index } : {}),
            ...(run?.actionIndex != null ? { actionIndex: run.actionIndex } : {}),
            ...(run?.generation_index != null ? { generation_index: run.generation_index } : {}),
            ...(run?.generationIndex != null ? { generationIndex: run.generationIndex } : {}),
            ...(run?.status ? { status: run.status } : {}),
            output_urls: hasArrayItems(run?.output_urls)
                ? retainHeightRelevantMediaEntries(run.output_urls, (output: any) =>
                      aiRunOutputIsConsumedInput(run, output, aiInputDescriptors),
                  ).map((output: any) => {
                      const resolvedKind = payloadMediaKind(output)
                      return {
                          ...(resolvedKind === 'audio'
                              ? { kind: 'audio' }
                              : output?.kind
                              ? { kind: output.kind }
                              : {}),
                          ...(output?.type ? { type: output.type } : {}),
                          ...(output?.mime ? { mime: output.mime } : {}),
                          ...(output?.content_type ? { content_type: output.content_type } : {}),
                          ...dimsOnly(output, 'width', 'height'),
                          ...mediaTimingFingerprint(output, run?.created_at || run?.createdAt),
                          ...(combinedMediaItemReady({
                              url: mediaIdentityUrl(output),
                              status: run?.status,
                          })
                              ? { media_ready: 1 }
                              : {}),
                          ...(aiRunOutputIsConsumedInput(run, output, aiInputDescriptors)
                              ? { ai_input: 1 }
                              : {}),
                      }
                  })
                : [],
        }))
    }

    // A truly empty saved post otherwise collapses to an id-only fingerprint, which cannot be
    // recognized as a retained estimator source on the next signature pass. Keep the explicit
    // false gate only for that degenerate shape; normal text/media/poll fingerprints stay stable.
    const hasEstimatorInput = Object.keys(out).some(
        key => key !== 'id' && key !== 'height_content_signature',
    )
    if (!hasEstimatorInput && typeof projection?.hasPostAnswerContent === 'boolean') {
        out.post_answer_has_content = projection.hasPostAnswerContent
    }

    return out
}

const hashSignature = (input: string): string => {
    // djb2 -> base36: compact + deterministic. Same content bytes => same signature.
    let hash = 5381
    for (let i = 0; i < input.length; i++) {
        hash = (hash * 33) ^ input.charCodeAt(i)
    }
    return (hash >>> 0).toString(36)
}

// A compact, content-derived signature of an answer's height-determining inputs (the same inputs the
// estimators consume). Stable across a QuestChannel fan-out "touch" (which bumps updated_at without
// changing content) but changes on a genuine edit — used as the answerHeightKey revision term so a
// remembered measured height survives a touch (no on-screen height flap) while an edit still invalidates
// it. Returns null when the answer is not height-estimable (a bare stub), so the key falls back to 'none'.
// Memoized by answer-object identity: the entity cache replaces objects on every real write
// (never mutates in place — the same invariant React re-rendering relies on), so one
// fingerprint-extract + stringify + hash per object suffices. The virtualizer signature paths
// call this for every row on every render, which made the un-memoized form O(rows × answers)
// hashing on the scroll hot path.
const answerHeightSignatureMemo = new WeakMap<object, string | null>()

export function answerHeightContentSignature(answer: any): string | null {
    const memoizable = typeof answer === 'object' && answer !== null
    if (memoizable && answerHeightSignatureMemo.has(answer)) {
        return answerHeightSignatureMemo.get(answer) ?? null
    }

    let signature: string | null = null
    const fingerprint = extractAnswerHeightFingerprint(answer)
    if (fingerprint) {
        try {
            signature = hashSignature(JSON.stringify(fingerprint))
        } catch {
            signature = null
        }
    }

    if (memoizable) answerHeightSignatureMemo.set(answer, signature)
    return signature
}

export function rememberAnswerHeightFingerprint(answerId: unknown, answer: any): boolean {
    const id = answerId ? String(answerId) : null
    if (!id) return false
    const fingerprint = extractAnswerHeightFingerprint(answer)
    if (!fingerprint) return false

    // Delete before set so Map iteration order stays least-recent-first for LRU eviction.
    fingerprints.delete(id)
    fingerprints.set(id, fingerprint)
    stats.remembered++
    while (fingerprints.size > MAX_ANSWER_HEIGHT_FINGERPRINTS) {
        const oldestKey = fingerprints.keys().next().value
        if (oldestKey === undefined) break
        fingerprints.delete(oldestKey)
    }
    return true
}

export function getAnswerHeightFingerprint(answerId: unknown): Record<string, unknown> | null {
    const id = answerId ? String(answerId) : null
    if (!id) return null
    return fingerprints.get(id) || null
}

// Lightweight observability for QA: how often placeholder substitution fires vs misses.
const stats = { passthrough: 0, substituted: 0, missed: 0, remembered: 0 }
try {
    if (typeof window !== 'undefined') {
        ;(window as any).__answerHeightFingerprintStats = stats
    }
} catch {}

// The estimators' entry point: pass through estimable answers untouched; substitute the
// retained fingerprint for bare placeholders so estimates stay exact across cache eviction.
export function resolveHeightEstimationSource(answer: any): any {
    if (answerLooksHeightEstimable(answer)) {
        stats.passthrough++
        return answer
    }
    const fingerprint = getAnswerHeightFingerprint(answer?.id)
    if (fingerprint) stats.substituted++
    else stats.missed++
    return fingerprint || answer
}
